import { Injectable, Inject } from '@angular/core';
import { Router } from '@angular/router';
import { isbExperienceServiceToken, IsbExperienceService, MoneyProcessing, PBS } from '@nationwide/dgs-angular-billing-common';
import { Observable, of, tap, map, catchError } from 'rxjs';
import { PaymentPreferencesApiService } from '../../../../shared/services/payment-preferences-api/payment-preferences-api.service';
import { CancelScheduleFuturePaymentFlow } from './cancel-schedule-future-payment-flow.service';
import { SessionService } from '../../../../../../shared/session/session.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ManagePaymentPrefsAdapter } from '../../../shared/adapters/manage-payment-prefs-adapter';
import { LoggerService } from '../../../../../../shared/logger/logger.service';

import { EasternTimeDatePipe, RemoveDuplicatesPipe } from '@nationwide/internet-servicing-angular-pipes';
import { DateUtil } from '@nationwide/angular-timeline';

import { SharedRCAAdapter } from '../../../../../../shared/shared-base.adapter';
import { IsbSharedCommonHelperService } from '../../../../../../shared/isb-shared-common-helper.service';
type PaymentInfo = MoneyProcessing.PaymentInfo;
type RetriveCustomerAgreementResponse = PBS.RetriveCustomerAgreementResponse;

@Injectable()
export class CancelScheduleFuturePaymentService extends SharedRCAAdapter {

    constructor(
        private flow: CancelScheduleFuturePaymentFlow,
        private paymentPreferencesApi: PaymentPreferencesApiService,
        @Inject(isbExperienceServiceToken) private isbExperience: IsbExperienceService,
        protected session: SessionService,
        private easternTimeDatePipe: EasternTimeDatePipe,
        @Inject('logger') private logger: LoggerService,
        private managePaymentPrefsAdapter: ManagePaymentPrefsAdapter,
        private dateUtil: DateUtil,
        protected removeDuplicatesPipe: RemoveDuplicatesPipe,
        protected sharedService: IsbSharedCommonHelperService,
        private router: Router
    ) {
        super(
            removeDuplicatesPipe,
            session,
            sharedService
        );
    }

    retrieveScheduledPayment(): Observable<PaymentInfo> {
        return this.paymentPreferencesApi
            .cancelFutureSchedulePaymentsLanding().pipe(
                map((res) => {
                    const { retrieveCustomerAgreement, paymentsHistory } = res;
                    this.flow.save({ unmappedPaymentHistory: paymentsHistory });
                    if (paymentsHistory?.payments) {
                        const nextDueDate = this.easternTimeDatePipe.transform(new Date(this.managePaymentPrefsAdapter.fetchBillingAccountAmountDueDate(retrieveCustomerAgreement)), 'MM/dd/yyyy');
                        for (const payment of paymentsHistory.payments) {
                            if (payment.status === 'SCHEDULED') {
                                if (!payment.paymentDate) {
                                    this.logger.error('HARDFALL: CANCEL SCHEDULED FUTURE PAYMENT - No payment date available', {
                                        paymentInfo: payment,
                                        billingAccountNumber: this.session.billingAccountNumber
                                    });
                                    this.router.navigateByUrl('/personal/error/system-down');
                                    return;
                                }

                                const paymentDate = this.easternTimeDatePipe.transform(new Date(payment.paymentDate), 'MM/dd/yyyy');
                                this.flow.save({
                                    paymentDate,
                                    paymentAmount: payment.amount.toString(),
                                    paymentMethodType: this.getPaymentMethodType(payment),
                                    receiptId: payment.receiptId || payment.paymentTxnDetails?.receiptId,
                                    nextDueDate
                                });
                                return payment;
                            }
                        }
                    }
                })
            );
    }

    retrievePaymentDateFromPBS(rcaResponse: RetriveCustomerAgreementResponse): string {
        const billingAccount = this.getBillingAgreement(rcaResponse);
        const messages = this.getMessagesFromBillingDetail(billingAccount?.billingAccountDetail?.billingDetail);
        const scheduledPaymentMessage = messages?.find((message) => message.name?.toLowerCase().includes('scheduled'));
        const parameters = scheduledPaymentMessage?.parameter instanceof Array ? scheduledPaymentMessage?.parameter : [scheduledPaymentMessage?.parameter];
        const paymentDate = parameters?.find((parameter) => parameter.name === 'scheduledPaymentDate')?.value;
        return this.easternTimeDatePipe.transform(new Date(paymentDate), 'MM/dd/yyyy');
    }

    submit(): Observable<number | HttpErrorResponse> {
        return this.isbExperience.cancelScheduledPayment({
            agreementNumber: this.session.billingAccountNumber,
            accessToken: this.session.accessToken,
            billingSystem: this.session.billingSystem,
            paymentId: this.flow.receiptId,
            startDate: this.dateUtil.formatDateToString(this.startDate, '-'),
            endDate: this.dateUtil.formatDateToString(this.endDate, '-'),
            policyNumbers: this.session.policyNumbers,
            payload: {
                omsData: {
                    agreementNumber: this.session.billingAccountNumber,
                    paymentAmount: this.flow.paymentAmount,
                    receivedDate: this.flow.paymentDate
                },
                paymentMethodType: this.flow.paymentMethodType
            }
        }).pipe(
            tap((res) => this.flow.save({ apiCallSuccessful: true, apiCallResponseCode: res })),
            catchError((err: HttpErrorResponse) => {
                this.logger.error('API ERROR: CANCEL SCHEDULED FUTURE PAYMENT SUBMISSION', err);
                this.flow.save({ apiCallSuccessful: false, apiCallResponseCode: err.status });
                return of(err);
            })
        );
    }

    private getPaymentMethodType(payment: PaymentInfo): string {
        return payment.paymentTxnDetails?.electronicFundsTransfer ?
            'EFT' :
            'Bankcard';
    }

    private get startDate(): Date {
        const MONTHS_IN_TERM = 11;
        return new Date(this.endDate.getFullYear(), this.endDate.getMonth() - MONTHS_IN_TERM, this.endDate.getDate());
    }

    private get endDate(): Date {
        return new Date();
    }
}
